import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Add from '@mui/icons-material/Add'
import Title from '../../component/Title'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'
import { renderDateTime } from '../../utils/dateDisplay'
import { formatReserveTime } from './pushReserveTime'

const DEFAULT_SEARCH_SELECT = {
    searchStatus: 99,
    searchPopupStatus: '',
}

const SEARCH_SELECT_PARAMS = {
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
        parse: (value, defaultValue) => {
            if (value === null || value === undefined || value === '') {
                return defaultValue
            }

            const parsedValue = Number(value)
            return Number.isNaN(parsedValue) ? defaultValue : parsedValue
        },
    },
    searchPopupStatus: {
        param: 'popup',
        defaultValue: DEFAULT_SEARCH_SELECT.searchPopupStatus,
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const APP_PUSH_TABLE_COLUMNS = [
    { width: 70 },
    { width: 100 },
    { width: 110 },
    { width: 90 },
    { width: 110 },
    { width: 120 },
    { width: 130 },
    { width: 320 },
    { width: 230 },
    { width: 180 },
    { width: 120 },
]

const APP_PUSH_TABLE_MIN_WIDTH = APP_PUSH_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const PUSH_CONTENT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const DATE_RANGE_CELL_SX = {
    whiteSpace: 'nowrap',
}

const formatWeekType = (weekType) => {
    if (!weekType) {
        return '-'
    }

    return weekType
        .replace('1', '월')
        .replace('2', '화')
        .replace('3', '수')
        .replace('4', '목')
        .replace('5', '금')
        .replace('6', '토')
        .replace('7', '일')
}

export default function AppPushs() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText] = useState(initialListState.searchText)
    const [searchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const deleteInFlightRef = useRef(false)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appPushHistory/getList', { page: curPage, offset: offset, searchText: searchText, searchSelect: searchSelect })
            // console.log(data)
            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 푸시 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 푸시 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchSelect, searchText])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery: searchText, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchSelect, searchText])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handleDelete = (id) => {
        if (deleteInFlightRef.current) {
            return
        }

        deleteInFlightRef.current = true
        setActionLoading(true)

        const deleteData = async () => {
            try {
                const { data } = await API.post('/api/v1/admin/appPushHistory/delete', { pushHistoryId: id })
                if (data.code === 200) {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('success')
                    setMessageSnackbar(data.message)
                    if (curPage > 1 && params.length === 1) {
                        setCurPage(curPage - 1)
                    } else {
                        await getData(false)
                    }
                } else if (data.code === 403) {
                    history.push('/login')
                } else {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(data.message)
                }
            } catch (_error) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('App 푸시를 삭제하지 못했습니다.')
            } finally {
                deleteInFlightRef.current = false
                setActionLoading(false)
            }
        }
        deleteData()
    }

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="App 푸시 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={actionLoading}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        return (
            <ListEmptyState
                title="등록된 App 푸시가 없습니다."
                description="App 푸시를 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {/* 타이틀 */}
                    <Title>App 푸시 리스트</Title>
                    
                    {/* 테이블 */}
                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-appPush',
                                    search: location.search,
                                    curPage: curPage,
                                    searchText: searchText,
                                    searchSelect: searchSelect
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: APP_PUSH_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                        <colgroup>
                            {APP_PUSH_TABLE_COLUMNS.map((column, index) => (
                                <col key={`app-push-column-${index}`} style={{ width: column.width }} />
                            ))}
                        </colgroup>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                {/* <TableCell>이미지</TableCell> */}
                                <TableCell>노출나라</TableCell>
                                <TableCell>기종</TableCell>
                                <TableCell>액션</TableCell>
                                <TableCell>발송</TableCell>
                                <TableCell>시간</TableCell>
                                <TableCell>요일</TableCell>
                                <TableCell>푸시 내용</TableCell>
                                <TableCell>시작일<br/>종료일</TableCell>
                                <TableCell>등록일</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? (
                                params.map((row) => (
                                    <TableRow key={row.pushHistoryId}>
                                        <TableCell>{row.rownum}</TableCell>
                                        <TableCell>{row.cCode === 'kr' ? '국내' : '해외'}</TableCell>
                                        <TableCell>{row.deviceType === 0 ? '전체' : row.deviceType === 1 ? 'Android' : 'iOS'}</TableCell>
                                        <TableCell>{row.pushType === 1 ? '홈' : row.pushType === 2 ? '대회' : '최저가'}</TableCell>
                                        <TableCell>{row.isReserve === 0 ? '즉시발송' : '예약발송'}</TableCell>
                                        <TableCell>{row.isReserve === 0 ? '' : formatReserveTime(row.reserveHour, row.reserveMinute, row.timeType)}</TableCell>
                                        <TableCell>{formatWeekType(row.weekType)}</TableCell>
                                        <TableCell className="listTableLimitText" sx={PUSH_CONTENT_CELL_SX}>{row.content || '-'}</TableCell>
                                        <TableCell sx={DATE_RANGE_CELL_SX}>{row.startTime || '-'}<br/>{row.endTime || '-'}</TableCell>
                                        <TableCell sx={DATE_RANGE_CELL_SX}>{renderDateTime(row.regTime)}</TableCell>
                                        <TableCell align="right">
                                            <ListActionGroup disabled={actionLoading}>
                                                <EditIcon path={'appPush'} id={row.pushHistoryId} search={location.search} curPage={curPage} searchText={searchText} searchSelect={searchSelect}/>
                                                <DeleteAlertDialog handleDelete={handleDelete} id={row.pushHistoryId}/>
                                            </ListActionGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={11}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
